import React from 'react';
import { Heart, Moon, Flame, BarChart3, Smartphone, Plus } from '@/utils/icons';
import { READINESS_SCORE_TOOLTIP } from '@/constants/tooltips';
import { InfoTooltip } from '@/components/shared';
import { isAnyWearableConnected } from '@/config/wearableProviders';
import { mergeRosterIntoData } from '@/utils/mergeRoster';
import { TIER_CONFIG } from '@/constants/injuryRisk';

interface AthletesTabProps {
    selectedTeam: any;
    isDark: boolean;
    handleAthleteDoubleClick: (athleteId: number) => void;
    /** Shown only for coach/admin; triggers Create User modal */
    onAddAthlete?: () => void;
    userRole?: string;
    riskScoreMap?: Map<number, { tier: string; risk_score: number }>;
}

function getReadinessColor(value: number) {
  if (value >= 75) return '#22C55E';
  if (value >= 55) return '#F5D76E';
  return '#EF4444';
}
function getSleepColor() {
  return '#7BA1BB';
}
function getStrainColor() {
  return '#0093E7';
}

const AthletesTab: React.FC<AthletesTabProps> = ({
    selectedTeam,
    isDark,
    handleAthleteDoubleClick,
    onAddAthlete,
    userRole,
    riskScoreMap,
}) => {
    const canAddAthlete = onAddAthlete && (userRole === 'coach' || userRole === 'admin');

    // Merge the team roster with data endpoints so newly-created users
    // appear immediately even while the backend readiness cache warms up.
    const useUnifiedData = !!selectedTeam?.readiness_data?.all_athletes;
    const dataAthletes: any[] = selectedTeam?.readiness_data?.all_athletes
        || selectedTeam?.recovery_data?.athletes_data
        || [];
    const roster: any[] = selectedTeam?.athletes || [];

    const athletesData = React.useMemo(
        () => mergeRosterIntoData(dataAthletes, roster, useUnifiedData),
        [dataAthletes, roster, useUnifiedData],
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {athletesData.map((athlete: any) => {
                let readinessScore: number;
                let readinessTier: string;
                let cleanSleepStats;
                let cleanActivityStats;
                let athleteId: number;
                let athleteName: string;
                let isConnected: boolean;
                let dataPoints: number;

                if (useUnifiedData) {
                    athleteId = athlete.id;
                    athleteName = athlete.name;
                    // Check connection flags OR actual data presence (handles cases where
                    // flags aren't propagated but wearable data exists)
                    isConnected = isAnyWearableConnected(athlete)
                        || (athlete.score != null && athlete.score > 0)
                        || (athlete.recovery?.data_points > 0)
                        || (athlete.sleep?.data_points > 0)
                        || (athlete.strain?.data_points > 0);

                    readinessScore = (athlete.score || 0) * 10;
                    readinessTier = athlete.tier || 'unknown';
                    dataPoints = athlete.recovery?.data_points || 0;

                    cleanSleepStats = {
                        averageQuality: athlete.sleep?.average_quality || 0,
                        averageDuration: athlete.sleep?.average_duration || 0,
                        latestQuality: athlete.sleep?.latest_quality || 0,
                        latestDuration: athlete.sleep?.latest_duration || 0,
                        dataPoints: athlete.sleep?.data_points || 0
                    };

                    cleanActivityStats = {
                        averageStrain: athlete.strain?.average || 0,
                        latestStrain: athlete.strain?.latest || 0,
                        dataPoints: athlete.strain?.data_points || 0
                    };
                } else {
                    athleteId = athlete.athlete_id;
                    athleteName = athlete.athlete_name;
                    isConnected = isAnyWearableConnected(athlete)
                        || (athlete.recovery_data?.length > 0);

                    const recoveryData = athlete.recovery_data?.filter(((data: any) => data && data.recovery_score))
                        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
                    const sleepData = selectedTeam.sleep_data?.athletes_data
                        .find((a: any) => a.athlete_name === athlete.athlete_name)?.sleep_data
                        ?.filter(((data: any) => data && data.total_duration_hours))
                        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];
                    const activityData = selectedTeam.activity_data?.athletes_data
                        .find((a: any) => a.athlete_name === athlete.athlete_name)?.activity_data
                        ?.filter(((data: any) => data && data.strain_score))
                        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [];

                    const avgRecovery = recoveryData.length > 0 ? recoveryData.reduce((sum: number, d: any) => sum + d.recovery_score, 0) / recoveryData.length : 0;
                    readinessScore = Math.round(avgRecovery * 10) / 10;
                    readinessTier = avgRecovery >= 75 ? 'OPTIMAL' : avgRecovery >= 55 ? 'MODERATE' : 'LOW';
                    dataPoints = recoveryData.length;

                    cleanSleepStats = {
                        averageQuality: sleepData.length > 0 ? Math.round(sleepData.reduce((sum: number, d: any) => sum + (d.sleep_quality_score || 0), 0) / sleepData.length) : 0,
                        averageDuration: sleepData.length > 0 ? Math.round((sleepData.reduce((sum: number, d: any) => sum + d.total_duration_hours, 0) / sleepData.length) * 10) / 10 : 0,
                        latestQuality: sleepData.length > 0 ? Math.round(sleepData[sleepData.length - 1].sleep_quality_score || 0) : 0,
                        latestDuration: sleepData.length > 0 ? Math.round(sleepData[sleepData.length - 1].total_duration_hours * 10) / 10 : 0,
                        dataPoints: sleepData.length
                    };

                    cleanActivityStats = {
                        averageStrain: activityData.length > 0 ? Math.round((activityData.reduce((sum: number, d: any) => sum + d.strain_score, 0) / activityData.length) * 10) / 10 : 0,
                        latestStrain: activityData.length > 0 ? Math.round(activityData[activityData.length - 1].strain_score * 10) / 10 : 0,
                        dataPoints: activityData.length
                    };
                }

                return (
                    <div
                        key={athleteId}
                        className={`cgm-card rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl transform hover:scale-105 transition-all duration-300 cursor-pointer group`}
                        onDoubleClick={() => handleAthleteDoubleClick(athleteId)}
                    >
                        <div className="flex flex-col h-full">
                            <div className="flex items-start justify-between gap-2 mb-4">
                                <div className="flex items-center space-x-3 min-w-0">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-900'}`}>
                                        {athleteName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <h3 className="text-lg font-semibold theme-text-primary group-hover:text-blue-600 transition-colors truncate">
                                            {athleteName}
                                        </h3>
                                        {riskScoreMap?.has(athleteId) && (() => {
                                            const risk = riskScoreMap.get(athleteId)!;
                                            const config = TIER_CONFIG[risk.tier];
                                            if (!config) return null;
                                            return (
                                                <span
                                                    className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                                                    style={{ backgroundColor: `${config.color}20`, color: config.color }}
                                                >
                                                    {config.label} Risk
                                                </span>
                                            );
                                        })()}
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                            <p className="text-sm theme-text-secondary">
                                                {isConnected ? 'Connected' : 'No Data'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isConnected && (
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="cgm-stat-success rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs theme-text-secondary font-medium">Readiness</span>
                                                <InfoTooltip
                                                    title={READINESS_SCORE_TOOLTIP.title}
                                                    body={READINESS_SCORE_TOOLTIP.body}
                                                    isDark={isDark}
                                                    iconSize={3}
                                                    ariaLabel="How Readiness Score is calculated"
                                                />
                                            </div>
                                            <Heart className="w-4 h-4 cgm-text-primary" />
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            <span style={{ color: getReadinessColor(readinessScore) }}>{readinessScore > 0 ? `${readinessScore.toFixed(0)}%` : '—'}</span>
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            <span style={{ color: getReadinessColor(readinessScore) }}>{readinessTier}</span>
                                        </div>
                                    </div>

                                    {/* Sleep */}
                                    <div className="cgm-stat-accent rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Sleep</span>
                                            <Moon className="w-4 h-4 cgm-text-primary" />
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            <span style={{ color: getSleepColor() }}>
                                                {cleanSleepStats.latestDuration > 0 ? `${cleanSleepStats.latestDuration.toFixed(1)}h` : '—'}
                                            </span>
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            Avg: {cleanSleepStats.averageDuration > 0 ? `${cleanSleepStats.averageDuration.toFixed(1)}h` : '—'}
                                        </div>
                                    </div>

                                    <div className="cgm-stat-warning rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Strain</span>
                                            <Flame className="w-4 h-4 cgm-text-primary" />
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            <span style={{ color: getStrainColor() }}>{cleanActivityStats.latestStrain > 0 ? `${cleanActivityStats.latestStrain.toFixed(1)}` : '—'}</span>
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            Avg: <span style={{ color: getStrainColor() }}>{cleanActivityStats.averageStrain > 0 ? `${cleanActivityStats.averageStrain.toFixed(1)}` : '—'}</span>
                                        </div>
                                    </div>

                                    <div className="cgm-stat-info rounded-xl p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs theme-text-secondary font-medium">Data</span>
                                            <BarChart3 className="w-4 h-4 cgm-text-primary" />
                                        </div>
                                        <div className="text-lg font-bold theme-text-primary">
                                            {Math.max(dataPoints || 0, cleanSleepStats.dataPoints || 0, cleanActivityStats.dataPoints || 0)}
                                        </div>
                                        <div className="text-xs theme-text-secondary">
                                            days
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isConnected && (
                                <div className="cgm-code-display p-4 mb-4 text-center">
                                    <div className="mb-2 flex justify-center">
                                        <Smartphone className="w-8 h-8 cgm-text-secondary" />
                                    </div>
                                    <p className="text-sm theme-text-secondary">No wearable data available</p>
                                </div>
                            )}

                            <div className="mt-auto">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAthleteDoubleClick(athleteId);
                                    }}
                                    className="w-full cgm-button px-4 py-2 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
                                >
                                    View Details
                                </button>
                            </div>

                            <div className="mt-2 text-center">
                                <p className="text-xs theme-text-secondary opacity-50">
                                    Double-click for AI insights
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })}

            {canAddAthlete && athletesData.length > 0 && (
                <button
                    type="button"
                    onClick={onAddAthlete}
                    className={`cgm-card rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl transform hover:scale-105 transition-all duration-300 cursor-pointer group flex flex-col items-center justify-center min-h-[200px] text-center`}
                >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-gray-700/50 group-hover:bg-gray-600/50' : 'bg-gray-100 group-hover:bg-gray-200'}`}>
                        <Plus className="w-7 h-7 cgm-text-secondary" />
                    </div>
                    <span className="text-sm font-medium cgm-text-secondary">Add athlete</span>
                </button>
            )}

            {(athletesData.length === 0) && (
                <div className={`col-span-full cgm-card rounded-2xl p-12 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl text-center`}>
                    <div className={`w-24 h-24 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold theme-text-primary mb-4">No Athletes Found</h3>
                    <p className="text-lg theme-text-secondary max-w-2xl mx-auto leading-relaxed">
                        This team doesn't have any athletes with data for the selected time period. Athletes need to connect a wearable device to appear here.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AthletesTab;
